/**
 * Utility functions for Admin Panel
 */

/**
 * Format date for display (DD MMM YYYY)
 * @param {string|Date} date 
 * @param {string} locale 
 * @returns {string}
 */
export function formatDate(date, locale = 'en-GB') {
    if (!date || date === 'Unknown' || date === 'Not Specified') return '—'

    let d

    // ✅ Case 1: Already valid ISO
    d = new Date(date)

    // ❌ Case 2: TED format "2026-05-29+01:00"
    if (isNaN(d.getTime()) && typeof date === 'string' && date.includes('+')) {
        const cleaned = date.split('+')[0]
        d = new Date(cleaned + 'T00:00:00')
    }

    // ❌ Case 3: "30 September 2028, 11:59pm"
    if (isNaN(d.getTime()) && typeof date === 'string') {
        d = new Date(Date.parse(date))
    }

    if (isNaN(d.getTime())) return '—'

    return d.toLocaleDateString(locale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    })
}

/**
 * Format date and time with explicit timezone
 * @param {string|Date} date 
 * @returns {string}
 */
export function formatDateTime(date) {
    if (!date) return '—'
    const d = new Date(date)
    if (isNaN(d.getTime())) return String(date)

    const dateStr = formatDate(d)
    const timeStr = d.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    })

    // Determine primitive DST check for UK (GMT/BST)
    // Approximate: BST is last Sunday in March to last Sunday in October
    const month = d.getMonth()
    const isBST = month > 2 && month < 10
    const zone = isBST ? '(BST)' : '(GMT)'

    return `${dateStr} ${timeStr} ${zone}`
}

/**
 * Format relative time (e.g., "2 hours ago")
 * @param {string|Date} date 
 * @returns {string}
 */
export function formatRelativeTime(date) {
    if (!date) return '—'
    const d = new Date(date)
    const now = new Date()
    const diff = now - d

    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)

    if (minutes < 1) return 'Just now'
    if (minutes < 60) return `${minutes} min ago`
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`
    if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`
    return formatDate(date)
}

/**
 * Format currency
 * @param {number} amount 
 * @param {string} currency 
 * @returns {string}
 */
export function formatCurrency(amount, currency = 'GBP') {
    return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency,
    }).format(amount)
}

/**
 * Debounce function for search inputs
 * @param {Function} fn 
 * @param {number} delay 
 * @returns {Function}
 */
export function debounce(fn, delay = 300) {
    let timeoutId
    return (...args) => {
        clearTimeout(timeoutId)
        timeoutId = setTimeout(() => fn(...args), delay)
    }
}

/**
 * Class name combiner (like clsx)
 * @param  {...any} classes 
 * @returns {string}
 */
export function cn(...classes) {
    return classes.filter(Boolean).join(' ')
}

/**
 * Get RAG status for ingestion log
 * @param {Object} log 
 * @returns {{status: 'green'|'amber'|'red', label: string, color: string}}
 */
export function getIngestionRagStatus(log) {
    if (!log) return { status: 'green', label: 'Unknown', color: 'bg-gray-100 text-gray-500', dotClass: 'bg-gray-400', description: 'Status unknown.' }

    // RED: Critical failures
    if (log.status === 'failed' || log.failureStatus === 'open') {
        return {
            status: 'red',
            label: 'Intervention Required',
            color: 'bg-status-error-bg text-status-error',
            dotClass: 'bg-status-error',
            description: 'Repeated failures, stalled pipelines, or missed ingestion cycles.'
        }
    }

    // AMBER: Warning or self-correcting
    if (log.status === 'warning' || log.failureStatus === 'retried' || (log.errors > 0 && log.errors < 5)) {
        return {
            status: 'amber',
            label: 'Degraded',
            color: 'bg-status-warning-bg text-status-warning',
            dotClass: 'bg-status-warning',
            description: 'Temporary API timeouts, schema mismatches, or retries in progress.'
        }
    }

    // GREEN: Normal operation
    if (log.status === 'success') {
        return {
            status: 'green',
            label: 'Operational',
            color: 'bg-status-success-bg text-status-success',
            dotClass: 'bg-status-success',
            description: 'Ingestion running as expected, no or trivial transient errors.'
        }
    }

    // IDLE: Not configured or inactive
    return {
        status: 'gray',
        label: 'Idle',
        color: 'bg-gray-100 text-admin-muted',
        dotClass: 'bg-gray-400',
        description: 'Source is not actively ingesting data.'
    }
}
